/* global BigInt */
// The canonical `numeric-v1` value (Senior Phase primitives contract §2.3).
//
// Values are base-10 strings so JSON never turns them into floating point.
// Comparison converts every kind to an exact rational (BigInt numerator and
// positive denominator), so 1/2, 0.5 and 50% have the same value, while the
// authored representations still decide what the learner may submit.

export const NUMERIC_FORMAT = "numeric-v1";
export const REPRESENTATIONS = ["integer", "decimal", "fraction", "percentage"];

const INTEGER = /^-?(0|[1-9]\d*)$/;
// A decimal may be written without a fractional part ("36" is a decimal
// number); never scientific notation.
const DECIMAL = /^-?(0|[1-9]\d*)(\.\d+)?$/;
const DENOMINATOR = /^[1-9]\d*$/;

const isObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

// Whether `value` is a well-formed canonical numeric-v1 value.
export const isNumericValue = (value) => {
  if (!isObject(value) || value.format !== NUMERIC_FORMAT) return false;
  switch (value.kind) {
    case "integer":
      return typeof value.value === "string" && INTEGER.test(value.value);
    case "decimal":
    case "percentage":
      return typeof value.value === "string" && DECIMAL.test(value.value);
    case "fraction":
      return (
        typeof value.numerator === "string" &&
        INTEGER.test(value.numerator) &&
        typeof value.denominator === "string" &&
        DENOMINATOR.test(value.denominator)
      );
    default:
      return false;
  }
};

// Exact arithmetic needs BigInt. It is called, never written as a literal
// or raised with the exponent operator (which older targets compile to
// Math.pow), so the bundle still parses and the rest of the app still runs on
// a browser without it; the adapter then refuses numeric activities calmly
// (see exactNumbersSupported).
export const exactNumbersSupported = () => typeof BigInt === "function";
const B = (value) => BigInt(value);
const tenTo = (exponent) => {
  let result = B(1);
  for (let i = 0; i < exponent; i += 1) result *= B(10);
  return result;
};

const abs = (n) => (n < B(0) ? -n : n);
const gcd = (a, b) => {
  a = abs(a);
  b = abs(b);
  while (b) [a, b] = [b, a % b];
  return a || B(1);
};

const rational = (n, d) => {
  if (d === B(0)) return null;
  if (d < B(0)) [n, d] = [-n, -d];
  const g = gcd(n, d);
  return { n: n / g, d: d / g };
};

const decimalRational = (text) => {
  const negative = text.startsWith("-");
  const [whole, fraction = ""] = text.replace("-", "").split(".");
  const n = B(whole + fraction) * (negative ? -B(1) : B(1));
  return rational(n, tenTo(fraction.length));
};

// The exact value of a canonical numeric value, or null if it has none.
export const toRational = (value) => {
  if (!isNumericValue(value)) return null;
  switch (value.kind) {
    case "integer":
      return rational(B(value.value), B(1));
    case "decimal":
      return decimalRational(value.value);
    case "fraction":
      return rational(B(value.numerator), B(value.denominator));
    case "percentage": {
      const r = decimalRational(value.value);
      return rational(r.n, r.d * B(100));
    }
    default:
      return null;
  }
};

const sub = (a, b) => rational(a.n * b.d - b.n * a.d, a.d * b.d);
const absR = (a) => ({ n: abs(a.n), d: a.d });
const lte = (a, b) => a.n * b.d <= b.n * a.d;
const mul = (a, b) => rational(a.n * b.n, a.d * b.d);

export const isZero = (value) => {
  const r = toRational(value);
  return Boolean(r && r.n === B(0));
};

export const isNegative = (value) => {
  const r = toRational(value);
  return Boolean(r && r.n < B(0));
};

// A well-formed tolerance: absolute or relative with a non-negative value.
export const isTolerance = (tolerance) =>
  isObject(tolerance) &&
  (tolerance.type === "absolute" || tolerance.type === "relative") &&
  isNumericValue(tolerance.value) &&
  !isNegative(tolerance.value);

// Exact equality by default; an authored tolerance only when given. Relative
// tolerance is a ratio of the expected value (contract §3.3).
export const numericEqual = (actual, expected, tolerance = null) => {
  const a = toRational(actual);
  const e = toRational(expected);
  if (!a || !e) return false;
  const difference = absR(sub(a, e));
  if (!tolerance) return difference.n === B(0);
  const t = toRational(tolerance.value);
  const allowed = tolerance.type === "relative" ? mul(t, absR(e)) : t;
  return lte(difference, allowed);
};

// --- Learner entry ---------------------------------------------------------

// The decimal separator the learner sees: the device's own (a comma in
// South African schools), while persistence always uses ".".
export const decimalSeparator = () => {
  try {
    return (
      new Intl.NumberFormat(undefined)
        .formatToParts(1.5)
        .find((part) => part.type === "decimal")?.value || "."
    );
  } catch {
    return ".";
  }
};

const canonicalInteger = (digits, negative) => {
  const trimmed = digits.replace(/^0+(?=\d)/, "");
  return trimmed === "0" || !negative ? trimmed : `-${trimmed}`;
};

const canonicalDecimal = (text, negative) => {
  const [whole, fraction] = text.split(".");
  const body = `${whole.replace(/^0+(?=\d)/, "") || "0"}.${fraction}`;
  return negative && /[1-9]/.test(body) ? `-${body}` : body;
};

// Reads what the learner typed into a canonical value, within the authored
// representations. Accepts "," or "." as the decimal separator and the
// Unicode minus. Returns { value } or { error } with a reason the screen can
// explain: "empty", "malformed", "zero-denominator", "negative" or
// "representation" (the representation is not allowed here).
export const parseNumericEntry = (
  text,
  { representations = REPRESENTATIONS, allowNegative = true } = {}
) => {
  const raw = String(text ?? "")
    .replace(/\s+/g, "")
    .replace(/[−–]/g, "-")
    .replace(/,/g, ".");
  if (!raw) return { error: "empty" };
  const negative = raw.startsWith("-");
  const unsigned = negative ? raw.slice(1) : raw;
  let value;
  let kind;

  if (/^\d+\/-?\d+$/.test(unsigned)) {
    kind = "fraction";
    const [numerator, denominator] = unsigned.split("/");
    const d = B(denominator);
    if (d === B(0)) return { error: "zero-denominator" };
    // Sign normalised onto the numerator; the denominator is positive.
    const n = B(numerator) * (negative !== (d < B(0)) ? -B(1) : B(1));
    value = {
      format: NUMERIC_FORMAT,
      kind,
      numerator: canonicalInteger(abs(n).toString(), n < B(0)),
      denominator: abs(d).toString(),
    };
  } else if (/^(\d+(\.\d+)?|\.\d+)%$/.test(unsigned)) {
    kind = "percentage";
    const number = unsigned.slice(0, -1);
    value = {
      format: NUMERIC_FORMAT,
      kind,
      value: number.includes(".")
        ? canonicalDecimal(number.startsWith(".") ? `0${number}` : number, negative)
        : canonicalInteger(number, negative),
    };
  } else if (/^(\d+\.\d+|\.\d+)$/.test(unsigned)) {
    kind = "decimal";
    value = {
      format: NUMERIC_FORMAT,
      kind,
      value: canonicalDecimal(unsigned.startsWith(".") ? `0${unsigned}` : unsigned, negative),
    };
  } else if (/^\d+$/.test(unsigned)) {
    // A whole number is an integer, or a decimal where only decimals are
    // asked for.
    kind = representations.includes("integer") ? "integer" : "decimal";
    value = {
      format: NUMERIC_FORMAT,
      kind,
      value: canonicalInteger(unsigned, negative),
    };
  } else {
    return { error: "malformed" };
  }

  if (!representations.includes(kind)) return { error: "representation" };
  if (!allowNegative && isNegative(value)) return { error: "negative" };
  return { value };
};

// How a canonical value is shown to the learner or teacher.
export const formatNumeric = (value, separator = ".") => {
  if (!isNumericValue(value)) return "";
  const minus = (text) => text.replace(/^-/, "−");
  switch (value.kind) {
    case "fraction":
      return `${minus(value.numerator)}/${value.denominator}`;
    case "percentage":
      return `${minus(value.value.replace(".", separator))}%`;
    default:
      return minus(value.value.replace(".", separator));
  }
};

// What the learner would type to get this value back (for restoring an
// entry): the same as the display, with the ASCII minus.
export const entryTextOf = (value, separator = ".") =>
  formatNumeric(value, separator).replace(/−/g, "-");
